import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { canonicalUrlForPath, getPageSeo, shouldNoIndexPath } from '../../seo/documentSeo';

function ensureMeta(attr: 'name' | 'property', key: string): HTMLMetaElement {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Per-route canonical, document title, meta description, and OG/Twitter tags.
 * React 19–safe (no react-helmet-async).
 */
const DocumentHead = () => {
  const { pathname } = useLocation();
  const canonicalUrl = canonicalUrlForPath(pathname);
  const { title, description } = getPageSeo(pathname);

  useLayoutEffect(() => {
    document.title = title;

    ensureMeta('name', 'description').setAttribute('content', description);
    ensureMeta('property', 'og:title').setAttribute('content', title);
    ensureMeta('property', 'og:description').setAttribute('content', description);
    ensureMeta('property', 'og:url').setAttribute('content', canonicalUrl);
    ensureMeta('property', 'twitter:title').setAttribute('content', title);
    ensureMeta('property', 'twitter:description').setAttribute('content', description);

    const selector = 'link[data-heal-canonical="1"]';
    let link = document.querySelector(selector) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      link.setAttribute('data-heal-canonical', '1');
      document.head.appendChild(link);
    }
    link.href = canonicalUrl;

    const robots = document.querySelector('meta[name="robots"]');
    if (shouldNoIndexPath(pathname)) {
      ensureMeta('name', 'robots').setAttribute('content', 'noindex, nofollow');
    } else {
      robots?.remove();
    }
  }, [pathname, canonicalUrl, title, description]);

  return null;
};

export default DocumentHead;
